import { useEffect, useState } from "react";
import { api, fmtNGN, fmtUSD, img, tierFor, tierStyle } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LayoutGrid, List, Loader2, Image as ImageIcon, Gavel } from "lucide-react";

export default function Holdings() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [view, setView] = useState("grid");

  useEffect(() => { api.get("/holdings").then((r) => setItems(r.data.items)); }, []);

  if (!items) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold">My Art Holdings</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} artwork{items.length !== 1 ? "s" : ""} in your collection.</p>
        </div>
        <div className="flex gap-1 bg-secondary rounded-full p-1">
          <button onClick={() => setView("grid")} data-testid="view-grid" className={`h-9 w-9 grid place-items-center rounded-full ${view === "grid" ? "bg-background shadow" : ""}`}><LayoutGrid className="h-4 w-4" /></button>
          <button onClick={() => setView("list")} data-testid="view-list" className={`h-9 w-9 grid place-items-center rounded-full ${view === "list" ? "bg-background shadow" : ""}`}><List className="h-4 w-4" /></button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-card border border-border rounded-3xl p-12 text-center">
          <ImageIcon className="h-10 w-10 text-muted-foreground mx-auto" />
          <h3 className="font-heading text-xl font-bold mt-4">No artworks yet</h3>
          <p className="text-sm text-muted-foreground mt-1">Purchase your first artwork to start reviewing and joining auctions.</p>
          <Button className="rounded-full mt-5 navy-gradient text-white font-semibold" onClick={() => nav("/gallery")} data-testid="empty-browse-btn">Browse Gallery</Button>
        </div>
      ) : view === "grid" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((h) => (
            <div key={h.id} className="bg-card border border-border rounded-2xl overflow-hidden" data-testid={`holding-${h.id}`}>
              <div className="aspect-[3/4] overflow-hidden bg-muted relative"><span className={`absolute top-2 left-2 z-10 text-[10px] font-bold px-2 py-0.5 rounded-full ${tierStyle(h.tier || tierFor(h.purchase_price_ngn))}`}>{h.tier || tierFor(h.purchase_price_ngn)}</span><img src={img(h.thumbnail || h.image)} alt={h.title} loading="lazy" className="h-full w-full object-cover" /></div>
              <div className="p-3">
                <h3 className="font-heading font-semibold text-sm truncate">{h.title}</h3>
                <p className="text-xs text-muted-foreground truncate">{h.artist}</p>
                <p className="font-bold text-sm mt-1">{fmtNGN(h.purchase_price_ngn)}</p>
                <p className="text-[11px] text-muted-foreground">Bought {new Date(h.purchase_date).toLocaleDateString()}</p>
                {h.auction_eligible && <span className="inline-flex items-center gap-1 text-[10px] text-gold font-bold mt-1"><Gavel className="h-3 w-3" /> Auction eligible</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((h) => (
            <div key={h.id} className="bg-card border border-border rounded-2xl p-3 flex items-center gap-4" data-testid={`holding-${h.id}`}>
              <img src={img(h.thumbnail || h.image)} alt={h.title} loading="lazy" className="h-16 w-16 rounded-xl object-cover bg-muted" />
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-semibold truncate">{h.title}</h3>
                <p className="text-xs text-muted-foreground truncate">{h.artist} • Bought {new Date(h.purchase_date).toLocaleDateString()}</p>
                <span className="inline-flex items-center gap-1 text-[10px] text-gold font-bold mt-0.5 capitalize">{h.status}</span>
              </div>
              <div className="text-right">
                <p className="font-bold">{fmtNGN(h.purchase_price_ngn)}</p>
                <p className="text-xs text-muted-foreground">{fmtUSD(h.purchase_price_usd)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
